import * as React from 'react';
import ShowSetting from '../questionSettings/showSetting';
import Toggle from '../../../../common/Toggle';
import { SettingBody } from '../settingsBox/SettingBox';
import { Question, QuestionSmartProbeSettings } from '@nexxt/common/types';

interface ProbeSettingsProps {
    settings: QuestionSmartProbeSettings;
    question: Question;
    disabled: boolean;
    setQuestion: (question: Question) => void;
}

const ProbeSettings = ({
    question,
    settings,
    disabled,
    setQuestion
}: ProbeSettingsProps): JSX.Element => {
    return (
        <div className="form-group p-4">
            <div className="font-semibold text-lg mb-4">
                SmartProbe Settings
            </div>
            <div className="grid grid-cols-2 gap-8">
                <ShowSetting keyName="showProbeToggle" setting={settings}>
                    <div className="flex flex-row gap-4">
                        <div className="flex-1">
                            <label className="font-semibold mb-1">
                                SmartProbe
                            </label>
                            <p>
                                Dynamically probes participant with a single
                                follow-up question to elicit more information.
                            </p>
                        </div>
                        <div className="w-10">
                            <Toggle
                                className="without-label -mt-3"
                                onChange={(value) =>
                                    setQuestion({
                                        ...question,
                                        showGenericProbe: value,
                                        conversationalSettings: {
                                            ...question.conversationalSettings,
                                            smartProbeSettings: {
                                                ...question
                                                    .conversationalSettings
                                                    .smartProbeSettings,
                                                enableSmartProbe: value,
                                                enableResearchContext: !value
                                                    ? value
                                                    : question
                                                          .conversationalSettings
                                                          .smartProbeSettings
                                                          .enableResearchContext,
                                                enableTrainedModels: !value
                                                    ? value
                                                    : question
                                                          .conversationalSettings
                                                          .smartProbeSettings
                                                          .enableTrainedModels
                                            }
                                        }
                                    })
                                }
                                checked={
                                    question.conversationalSettings
                                        .smartProbeSettings.enableSmartProbe ||
                                    question.showGenericProbe
                                }
                                disabled={disabled}
                            />
                        </div>
                    </div>
                </ShowSetting>
                <ShowSetting keyName="showTargets" setting={settings}>
                    <div className="flex flex-row gap-4">
                        <div className="flex-1">
                            <label className="font-semibold mb-1">
                                Conversational Targets
                            </label>
                            <p>
                                Detect and/or probe on targets of interest: by
                                adding conversational targets, you can help
                                guide SmartProbe. Targets may also be used as
                                inca variables (in survey logic and in dashboard
                                analytics).
                            </p>
                        </div>
                        <div className="w-10">
                            <Toggle
                                className="without-label -mt-3"
                                onChange={(value) =>
                                    setQuestion({
                                        ...question,
                                        conversationalSettings: {
                                            ...question.conversationalSettings,
                                            smartProbeSettings: {
                                                ...question
                                                    .conversationalSettings
                                                    .smartProbeSettings,
                                                enableTargets: value
                                            }
                                        }
                                    })
                                }
                                checked={
                                    question.conversationalSettings
                                        .smartProbeSettings.enableTargets
                                }
                                disabled={disabled}
                            />
                        </div>
                    </div>
                </ShowSetting>
                {question.conversationalSettings.smartProbeSettings
                    .enableSmartProbe && (
                    <ShowSetting
                        keyName="showResearchContext"
                        setting={settings}
                    >
                        <div className="flex flex-row gap-4">
                            <div className="flex-1">
                                <label className="font-semibold mb-1">
                                    Research Context
                                </label>
                                <p>
                                    Make SmartProbe more context-aware by
                                    providing 1-2 sentence description related
                                    to research objectives, content of stimulus,
                                    or the type of information you want to
                                    elicit.
                                </p>
                            </div>
                            <div className="w-10">
                                <Toggle
                                    className="without-label -mt-3"
                                    onChange={(value) =>
                                        setQuestion({
                                            ...question,
                                            conversationalSettings: {
                                                ...question.conversationalSettings,
                                                smartProbeSettings: {
                                                    ...question
                                                        .conversationalSettings
                                                        .smartProbeSettings,
                                                    enableResearchContext: value
                                                }
                                            }
                                        })
                                    }
                                    checked={
                                        question.conversationalSettings
                                            .smartProbeSettings
                                            .enableResearchContext
                                    }
                                    disabled={
                                        disabled ||
                                        !question.conversationalSettings
                                            .smartProbeSettings.enableSmartProbe
                                    }
                                />
                            </div>
                        </div>
                    </ShowSetting>
                )}
                {question.conversationalSettings.smartProbeSettings
                    .enableSmartProbe && (
                    <ShowSetting keyName="showTrainedModels" setting={settings}>
                        <div className="flex flex-row gap-4">
                            <div className="flex-1">
                                <label className="font-semibold mb-1">
                                    Trained Models
                                </label>
                                <p>
                                    Use pretrained models available in your
                                    account, or provide your own examples of
                                    ideal probe questions in order to steer
                                    SmartProbe's behaviour. For custom trained
                                    models including multi-turn, contact{' '}
                                    <a href="mailto:inca@nexxt.in">
                                        inca@nexxt.in
                                    </a>
                                </p>
                            </div>
                            <div className="w-10">
                                <Toggle
                                    className="without-label -mt-3"
                                    onChange={(value) =>
                                        setQuestion({
                                            ...question,
                                            conversationalSettings: {
                                                ...question.conversationalSettings,
                                                smartProbeSettings: {
                                                    ...question
                                                        .conversationalSettings
                                                        .smartProbeSettings,
                                                    enableTrainedModels: value
                                                }
                                            }
                                        })
                                    }
                                    checked={
                                        question.conversationalSettings
                                            .smartProbeSettings
                                            .enableTrainedModels
                                    }
                                    disabled={
                                        disabled ||
                                        !question.conversationalSettings
                                            .smartProbeSettings.enableSmartProbe
                                    }
                                />
                            </div>
                        </div>
                    </ShowSetting>
                )}
                <ShowSetting
                    keyName="showProbeToggleForLikeFollowUp"
                    setting={settings}
                >
                    <div className="flex flex-row gap-4">
                        <div className="flex-1">
                            <label className="font-semibold mb-1">
                                Enable Probe on Followup(Like)
                            </label>{' '}
                            <p>
                                When enabled, inca will detect when participants
                                respond with gibberish answers like
                                “lakshdfjashdfk” and prompt them to answer again
                            </p>
                        </div>
                        <div className="w-10">
                            <Toggle
                                className="without-label -mt-3"
                                onChange={(value) =>
                                    setQuestion({
                                        ...question,
                                        probeSettings: {
                                            ...question.probeSettings,
                                            followUpTextsLike: {
                                                showGenericProbe: value,
                                                lazyAnswer: value,
                                                gibberish: value
                                            }
                                        }
                                    })
                                }
                                checked={
                                    question.probeSettings?.followUpTextsLike
                                        ?.showGenericProbe
                                }
                                disabled={disabled}
                            />
                        </div>
                    </div>
                </ShowSetting>
                <ShowSetting
                    keyName="showProbeToggleForDislikeFollowUp"
                    setting={settings}
                >
                    <div className="flex flex-row gap-4">
                        <div className="flex-1">
                            <label className="font-semibold mb-1">
                                Enable Probe on Followup (Dislike)
                            </label>{' '}
                            <p>
                                When enabled, inca will detect when participants
                                respond with gibberish answers like
                                “lakshdfjashdfk” and prompt them to answer again
                            </p>
                        </div>
                        <div className="w-10">
                            <Toggle
                                className="without-label -mt-3"
                                onChange={(value) =>
                                    setQuestion({
                                        ...question,
                                        probeSettings: {
                                            ...question.probeSettings,
                                            followUpTextsDislike: {
                                                showGenericProbe: value,
                                                lazyAnswer: value,
                                                gibberish: value
                                            }
                                        }
                                    })
                                }
                                checked={
                                    question.probeSettings?.followUpTextsDislike
                                        ?.showGenericProbe
                                }
                                disabled={disabled}
                            />
                        </div>
                    </div>
                </ShowSetting>
            </div>
        </div>
    );
};

export default ProbeSettings;
